import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

export type ColorMode = 'light' | 'dark'

/** Shared with the pre-paint bootstrap script in index.html. */
export const COLOR_MODE_STORAGE_KEY = 'scout-color-mode'

interface ColorModeContextValue {
  colorMode: ColorMode
  setColorMode: (mode: ColorMode) => void
  toggleColorMode: () => void
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null)

function systemPreference(): ColorMode {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function storedPreference(): ColorMode | null {
  try {
    const value = localStorage.getItem(COLOR_MODE_STORAGE_KEY)
    return value === 'light' || value === 'dark' ? value : null
  } catch {
    // Private browsing, or site data blocked. Fall back to the system.
    return null
  }
}

/** Chakra's `_dark` condition matches `.dark &`, so the class goes on <html>. */
function applyColorMode(mode: ColorMode) {
  const root = document.documentElement
  root.classList.toggle('dark', mode === 'dark')
  root.classList.toggle('light', mode === 'light')
  root.style.colorScheme = mode
}

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [colorMode, setColorModeState] = useState<ColorMode>(
    () => storedPreference() ?? systemPreference(),
  )

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode)
    applyColorMode(mode)
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode)
    } catch {
      // Persisting is a convenience; the session still works without it.
    }
  }, [])

  // Follow the system until the user makes an explicit choice. The check is
  // inside the handler, not around the subscription, so clearing the stored
  // choice starts tracking the system again without a remount.
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)')

    const onChange = (event: MediaQueryListEvent) => {
      if (storedPreference() !== null) return

      const mode: ColorMode = event.matches ? 'dark' : 'light'
      setColorModeState(mode)
      applyColorMode(mode)
    }

    query.addEventListener('change', onChange)
    return () => {
      query.removeEventListener('change', onChange)
    }
  }, [])

  const value = useMemo<ColorModeContextValue>(
    () => ({
      colorMode,
      setColorMode,
      toggleColorMode: () => {
        setColorMode(colorMode === 'dark' ? 'light' : 'dark')
      },
    }),
    [colorMode, setColorMode],
  )

  return <ColorModeContext value={value}>{children}</ColorModeContext>
}

export function useColorMode(): ColorModeContextValue {
  const context = use(ColorModeContext)
  if (!context) {
    throw new Error('useColorMode must be used inside <Provider>')
  }
  return context
}
